import { Card, Icon, IconButton, Tooltip } from "@/components/atoms";
import { useOperator } from "@/hooks";
import { useAppDispatch } from "@/redux/hooks";
import { setInlineEditing } from "@/redux/slices/booking/bookingSlice";
import { BookingDetails } from "@/redux/slices/booking/types";
import { DetailField } from "../../../common/DetailField";
import { useIsOperatorOwnJob } from "../../../hooks/useIsOperatorOwnJob";

interface NotesSectionProps {
  booking: BookingDetails;
}

export const NotesSection = ({ booking }: NotesSectionProps) => {
  const { notes, driver, metadata, status } = booking;
  const dispatch = useAppDispatch();
  const isOwnJob = useIsOperatorOwnJob();
  const {
    flags: { dashboardNotesEnabled },
  } = useOperator();
  const isAcceptedFromNetwork = metadata.isAcceptedFromNetwork;

  const nonEditableStatuses = ["closed", "cancelled", "complete_awaiting_review"];
  const isEditable = (!metadata.isOffloaded || isOwnJob) && !nonEditableStatuses.includes(status);

  const handleEditDashboard = () => dispatch(setInlineEditing("dashboard-notes"));
  const handleEditClient = () => dispatch(setInlineEditing("client-notes"));
  const handleEditOperatorToDriver = () => dispatch(setInlineEditing("driver-notes"));
  const handleEditAdmin = () => dispatch(setInlineEditing("admin-notes"));
  const handleEditExternal = () => dispatch(setInlineEditing("external-supplier-notes"));

  return (
    <Card className="space-y-3 p-2.5">
      <DetailField
        leading={<Icon name="NoteText" size="lg" className="text-neutral-dark-gray" />}
        label="Client Notes to Driver"
        labelInlineRight={
          <Tooltip content="Notes from the client to the driver" placement="top">
            <Icon name="InfoCircle" className="inline-block translate-x-1 rotate-180 text-neutral-dark-gray" size="sm" />
          </Tooltip>
        }
        value={notes.driver || "-"}
        right={
          isEditable ? (
            <Tooltip content="Notes from the client to the driver" placement="top">
              <IconButton iconName="Edit2" iconSize="md" size="xs" variant="primary" className="h-8 w-8" onClick={handleEditClient} />
            </Tooltip>
          ) : null
        }
      />

      <DetailField
        leading={<Icon name="NoteText" size="lg" className="text-neutral-dark-gray" />}
        label="Operator Notes to Driver"
        labelInlineRight={
          <Tooltip content="Notes from the operator to the driver" placement="top">
            <Icon name="InfoCircle" className="inline-block translate-x-1 rotate-180 text-neutral-dark-gray" size="sm" />
          </Tooltip>
        }
        value={notes.operatorToDriver || "-"}
        right={
          isEditable ? (
            <Tooltip content="Notes from the operator to the driver" placement="top">
              <IconButton
                iconName="Edit2"
                iconSize="md"
                size="xs"
                variant="primary"
                className="h-8 w-8"
                onClick={handleEditOperatorToDriver}
              />
            </Tooltip>
          ) : null
        }
      />

      {!isAcceptedFromNetwork && (
        <>
          {dashboardNotesEnabled && (
            <DetailField
              leading={<Icon name="NoteText" size="lg" className="text-neutral-dark-gray" />}
              label="Dashboard Notes"
              labelInlineRight={
                <Tooltip content="Internal notes visible on Booking Manager dashboard." placement="top">
                  <Icon name="InfoCircle" className="inline-block translate-x-1 rotate-180 text-neutral-dark-gray" size="sm" />
                </Tooltip>
              }
              value={notes.dashboard || "-"}
              right={
                <Tooltip content="Internal notes visible on Booking Manager dashboard." placement="top">
                  <IconButton
                    iconName="Edit2"
                    iconSize="md"
                    size="xs"
                    variant="primary"
                    className="h-8 w-8"
                    onClick={handleEditDashboard}
                  />
                </Tooltip>
              }
            />
          )}

          {(!metadata.isOffloaded || isOwnJob) && (
            <DetailField
              leading={<Icon name="NoteText" size="lg" className="text-neutral-dark-gray" />}
              label="Admin Notes"
              labelInlineRight={
                <Tooltip content="Internal administrative notes visible to operators only" placement="top">
                  <Icon name="InfoCircle" className="inline-block translate-x-1 rotate-180 text-neutral-dark-gray" size="sm" />
                </Tooltip>
              }
              value={notes.admin || "-"}
              right={
                <Tooltip content="Internal administrative notes visible to operators only" placement="top">
                  <IconButton iconName="Edit2" iconSize="md" size="xs" variant="primary" className="h-8 w-8" onClick={handleEditAdmin} />
                </Tooltip>
              }
            />
          )}

          {driver && driver.type !== "driver" && (
            <DetailField
              leading={<Icon name="NoteText" size="lg" className="text-neutral-dark-gray" />}
              label="External Supplier Notes"
              labelInlineRight={
                <Tooltip content="Notes shared between operators and external suppliers" placement="top">
                  <Icon name="InfoCircle" className="inline-block translate-x-1 rotate-180 text-neutral-dark-gray" size="sm" />
                </Tooltip>
              }
              value={notes.external || "-"}
              right={
                isEditable ? (
                  <Tooltip content="Notes shared between operators and external suppliers" placement="top">
                    <IconButton
                      iconName="Edit2"
                      iconSize="md"
                      size="xs"
                      variant="primary"
                      className="h-8 w-8"
                      onClick={handleEditExternal}
                    />
                  </Tooltip>
                ) : null
              }
            />
          )}
        </>
      )}
    </Card>
  );
};
